/**
 * MobileDashboard.js - 모바일 메인 대시보드
 * 
 * 기능:
 * - 상단: 헤더 (삼선 버튼 등)
 * - 중앙: 3개 메뉴 버튼 (일정, 그룹, 달력)
 * - 하단: 네비게이션 바 (새로고침, 카메라, 채팅, 마이크)
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, LogOut, Calendar, User } from 'lucide-react';
import { auth } from '../../config/firebaseConfig';
import BottomNavigation from './BottomNavigation';
import { useToast } from '../../contexts/ToastContext';
import './styles/MobileDashboard.css';

const MobileDashboard = ({ user, isVoiceEnabled, setIsVoiceEnabled }) => {
   const navigate = useNavigate();
   const { showToast } = useToast();
   const [isSidebarOpen, setIsSidebarOpen] = useState(false);

   const handleRefresh = useCallback(async () => {
      showToast('새로고침 완료');
   }, [showToast]);

   const handleLogout = async () => {
      try {
         await auth.signOut();
         localStorage.removeItem('loginMethod');
         navigate('/auth');
      } catch (error) {
         console.error('Logout error:', error);
      }
   };

   // 메뉴 버튼 클릭 핸들러
   const handleScheduleClick = () => {
      navigate('/schedule');
   };

   const handleGroupClick = () => {
      navigate('/groups');
   };

   const handleCalendarClick = () => {
      navigate('/calendar');
   };

   return (
      <div className="mobile-dashboard">
         {/* 사이드바 오버레이 */}
         {isSidebarOpen && (
            <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>
         )}

         {/* 사이드바 */}
         <nav className={`mobile-sidebar ${isSidebarOpen ? 'open' : ''}`}>
            <div className="sidebar-header">
               <h2 className="sidebar-title">메뉴</h2>
               <button className="sidebar-close-btn" onClick={() => setIsSidebarOpen(false)}>✕</button>
            </div>
            <div className="sidebar-menu">
               <button className="sidebar-item" onClick={() => navigate('/')}>
                  🏠 홈으로
               </button>
               <button className="sidebar-item" onClick={() => navigate('/schedule')}>
                  📅 내 일정
               </button>
               <button className="sidebar-item" onClick={() => navigate('/groups')}>
                  👥 그룹
               </button>
               <button className="sidebar-item" onClick={() => navigate('/calendar')}>
                  📆 달력
               </button>
               <button className="sidebar-item" onClick={() => navigate('/settings')}>
                  ⚙️ 설정
               </button>
            </div>
         </nav>

         {/* 모바일 헤더 */}
         <header className="mobile-header">
            <div className="mobile-header-content">
               {/* 왼쪽: 햄버거 메뉴 + 로고 */}
               <div className="mobile-header-left">
                  <button className="mobile-menu-btn" onClick={() => setIsSidebarOpen(true)}>
                     <Menu size={24} />
                  </button>
                  <div className="mobile-logo-btn" onClick={() => navigate('/')}>
                     <div className="mobile-logo-wrapper">
                        <img src="/heyheylogo.png" alt="MeetAgent Logo" className="mobile-logo-img" />
                        <div className={`mobile-login-indicator ${user?.google?.refreshToken ? 'google' : 'local'}`}></div>
                     </div>
                     <h1 className="mobile-logo-text">MeetAgent</h1>
                  </div>
               </div>

               {/* 오른쪽: 버튼들 */}
               <div className="mobile-header-right">
                  {/* 프로필 버튼 */}
                  <button className="mobile-profile-btn" onClick={() => navigate('/settings')} title="설정">
                     {user && user.firstName ? user.firstName : <User size={18} />}
                  </button>

                  {/* 로그아웃 버튼 */}
                  <button
                     className="mobile-logout-btn"
                     onClick={handleLogout}
                     title="로그아웃">
                     <LogOut size={16} />
                  </button>
               </div>
            </div>
         </header>

         {/* 중앙 메뉴 버튼 영역 */}
         <main className="dashboard-content">
            <div className="menu-buttons-container">
               {/* 일정 버튼 */}
               <button 
                  className="menu-card schedule-card"
                  onClick={handleScheduleClick}
               >
                  <h2 className="card-title">일정</h2>
                  <p className="card-description">내 프로필 및 일정 관리</p>
               </button>

               {/* 그룹 버튼 */}
               <button 
                  className="menu-card group-card"
                  onClick={handleGroupClick}
               >
                  <h2 className="card-title">그룹</h2>
                  <p className="card-description">일정맞추기 그룹 목록</p>
               </button>

               {/* 달력 버튼 */}
               <button 
                  className="menu-card calendar-card"
                  onClick={handleCalendarClick}
               >
                  <h2 className="card-title">달력</h2>
                  <p className="card-description">전체 일정 달력 보기</p>
               </button>
            </div>
         </main>

         {/* 하단 네비게이션 바 */}
         <BottomNavigation
            onRefresh={handleRefresh}
         />

      </div>
   );
};

export default MobileDashboard;
